import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Package, Truck, Warehouse, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import StokSekme from '../../components/StokSekme';
import StokTablosu from '../../components/StokTablosu';
import StokAktarModal from '../../components/StokAktarModal';
import { formatCurrency } from '../../components/StockIndicator';
import TuglaTable from './TuglaTable';
import TuglaModal from './TuglaModal';
import { getTuglaByInsaat, getTuglaStats } from '@mocks/materials/tugla';
import { getStokHavuzu } from '@mocks/materials/stokHavuzu';
import { getHareketler, hareketTipleri } from '@mocks/materials/stokHareketleri';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Tugla() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const [activeTab, setActiveTab] = useState('kullanim');
    const [filterTur, setFilterTur] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isAktarOpen, setIsAktarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;
    const filteredData = useMemo(() => {
        let data = getTuglaByInsaat(insaatId);
        if (filterTur) data = data.filter(item => item.tur === filterTur);
        return data;
    }, [insaatId, filterTur]);

    const stats = useMemo(() => getTuglaStats(insaatId), [insaatId]);
    const stokHavuzuData = getStokHavuzu('tugla');
    const hareketlerData = getHareketler('tugla');

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };

    const handleSubmit = async (formData, isEdit) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 800));

        if (!isEdit && formData.supplierId && formData.kdvDahilToplam > 0) {
            addMockCariHareket(formData.supplierId, formData.insaatId || insaatId || 'c1', {
                type: 'purchase',
                subtype: 'fatura',
                amount: formData.kdvDahilToplam,
                date: formData.siparisTarihi || new Date().toISOString().split('T')[0],
                description: `Tuğla Alımı — ${formData.miktar?.toLocaleString()} adet ${formData.tur}`,
            });
        }

        toast.success(isEdit ? 'Tuğla siparişi güncellendi' : 'Yeni tuğla siparişi oluşturuldu');
        setIsLoading(false); setIsModalOpen(false); setSelectedItem(null);

        if (!isEdit && formData.supplierId && formData.kdvDahilToplam > 0) {
            setOdemePlanData({ amount: formData.kdvDahilToplam, companyName: formData.tedarikci });
            setIsOdemePlanOpen(true);
        }
    };

    const handleOdemePlanConfirm = ({ nextDueDate }) => {
        toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`);
        setIsOdemePlanOpen(false);
        setOdemePlanData(null);
    };

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success('Tuğla siparişi silindi');
        setIsLoading(false); setIsDeleteOpen(false); setSelectedItem(null);
    };

    const handleAktarim = async (data) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success(`${data.miktar} adet stok aktarıldı`);
        setIsLoading(false); setIsAktarOpen(false);
    };

    const statsCards = [
        { title: 'Toplam Sipariş', value: `${stats.toplamAdet.toLocaleString()} adet`, icon: Package, color: 'primary' },
        { title: 'Teslim Edilen', value: `${stats.teslimEdilen.toLocaleString()} adet`, icon: Truck, color: 'success' },
        { title: 'Stokta', value: `${stats.stokAdet.toLocaleString()} adet`, icon: Warehouse, color: 'info' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'warning' },
    ];

    const detailFields = [
        { key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' },
        { key: 'teslimTarihi', label: 'Teslim Tarihi', type: 'date' },
        { key: 'tedarikci', label: 'Tedarikçi' },
        { key: 'tur', label: 'Tuğla Türü' },
        { key: 'miktar', label: 'Miktar', type: 'number' },
        { key: 'paletSayisi', label: 'Palet Sayısı', type: 'number' },
        { key: 'birimFiyat', label: 'Birim Fiyat', type: 'currency' },
        { key: 'toplamFiyat', label: 'Toplam Tutar', type: 'currency' },
        { key: 'durum', label: 'Durum', type: 'status' },
        { key: 'kullanimAlani', label: 'Kullanım Alanı', fullWidth: true },
    ];

    const headerFilter = (
        <div className="flex items-center gap-3">
            <StokSekme activeTab={activeTab} onTabChange={setActiveTab} />
            {activeTab === 'kullanim' && (
                <select value={filterTur} onChange={(e) => setFilterTur(e.target.value)}
                    className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]">
                    <option value="">Tüm Türler</option>
                    <option value="13.5 Yatay Delikli">13.5 Yatay Delikli</option>
                    <option value="19 Yatay Delikli">19 Yatay Delikli</option>
                    <option value="Gazbeton Blok 10cm">Gazbeton 10cm</option>
                    <option value="Gazbeton Blok 15cm">Gazbeton 15cm</option>
                    <option value="Gazbeton Blok 20cm">Gazbeton 20cm</option>
                    <option value="Briket 20cm">Briket</option>
                </select>
            )}
        </div>
    );

    return (
        <>
            <MaterialPageLayout title="Tuğla & Duvar" subtitle="Kaba İnşaat" icon={Package}
                iconBgColor="bg-amber-700" backLink="/insaat/kaba" stats={statsCards}
                onAddClick={activeTab === 'kullanim' ? handleAdd : undefined} headerFilter={headerFilter}>
                {activeTab === 'kullanim' ? (
                    <TuglaTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
                ) : (
                    <StokTablosu stokData={stokHavuzuData} hareketData={hareketlerData} hareketTipleri={hareketTipleri} birim="adet" onAktarim={() => setIsAktarOpen(true)} />
                )}
            </MaterialPageLayout>

            <TuglaModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedItem(null); }}
                onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} insaatId={insaatId} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => { setIsDetailOpen(false); setSelectedItem(null); }}
                data={selectedItem} fields={detailFields} title="Tuğla Siparişi Detayı" />
            <StokAktarModal isOpen={isAktarOpen} onClose={() => setIsAktarOpen(false)} onSubmit={handleAktarim} malzemeTipi="tugla" stokData={stokHavuzuData} birim="adet" isLoading={isLoading} />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => { setIsDeleteOpen(false); setSelectedItem(null); }}
                onConfirm={handleConfirmDelete} title="Siparişi Sil"
                message={`"${selectedItem?.tedarikci}" tedarikçisinden alınan ${selectedItem?.miktar?.toLocaleString()} adet tuğla siparişini silmek istediğinize emin misiniz?`}
                confirmText="Evet, Sil" isLoading={isLoading} />
            <OdemePlanMiniModal
                isOpen={isOdemePlanOpen}
                onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }}
                onConfirm={handleOdemePlanConfirm}
                amount={odemePlanData?.amount}
                companyName={odemePlanData?.companyName}
            />
        </>
    );
}
